import { useEffect, useRef, useState } from 'react'
import { ChevronDown, GripVertical } from 'lucide-react'
import Modal from './ui/Modal'
import Button from './ui/Button'
import {
  readDashboardAssetLinks,
  writeDashboardAssetLinks,
  readCachedDashboardAssets,
  writeCachedDashboardAssets,
  fetchDashboardAssetLinksFromApi,
  saveDashboardAssetLinksToApi,
  normalizeAssetUrl,
  newAssetId,
  dashboardAssetLinkLooksDemo,
  type AssetPlatformId,
  type DashboardAssetLink,
} from '../lib/dashboardAssets'

type Props = {
  isOpen: boolean
  onClose: () => void
  userId: string | null
}

const inputClass =
  'w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-[rgb(var(--surface-2-rgb))] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent'

type PlatformDef = {
  id: AssetPlatformId
  label: string
  logo: string
  /** Invert in dark mode so marks stay visible on dark surfaces */
  darkInvert?: boolean
}

const PLATFORMS: PlatformDef[] = [
  { id: 'google-drive', label: 'Google Drive', logo: '/logos/assets/google-drive.svg' },
  { id: 'google-docs', label: 'Google Docs / Sheets', logo: '/logos/deck/googlesheets.svg' },
  { id: 'gmail', label: 'Gmail', logo: '/logos/deck/gmail.svg' },
  { id: 'dropbox', label: 'Dropbox', logo: '/logos/assets/dropbox.svg' },
  { id: 'onedrive', label: 'OneDrive', logo: '/logos/assets/onedrive.svg' },
  { id: 'icloud', label: 'iCloud', logo: '/logos/assets/icloud.svg' },
  { id: 'box', label: 'Box', logo: '/logos/assets/box.svg' },
  { id: 'notion', label: 'Notion', logo: '/logos/assets/notion.svg', darkInvert: true },
  { id: 'figma', label: 'Figma', logo: '/logos/assets/figma.svg' },
  { id: 'slack', label: 'Slack', logo: '/logos/deck/slack.svg' },
  { id: 'spotify', label: 'Spotify', logo: '/logos/deck/spotify.svg' },
  { id: 'wetransfer', label: 'WeTransfer', logo: '/logos/assets/wetransfer.svg' },
  { id: 'other', label: 'Other', logo: '/logos/assets/other.svg', darkInvert: true },
]

const PLATFORM_BY_ID = Object.fromEntries(PLATFORMS.map((p) => [p.id, p])) as Record<AssetPlatformId, PlatformDef>

function AssetsListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <ul className="space-y-2" aria-hidden>
      {Array.from({ length: rows }, (_, i) => (
        <li
          key={i}
          className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/40 px-3 py-2.5 animate-pulse"
        >
          <div className="h-6 w-6 rounded bg-gray-200 dark:bg-gray-700 shrink-0" />
          <div className="flex-1 min-w-0 space-y-2">
            <div className="h-4 w-2/5 max-w-[200px] rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-3 w-3/5 max-w-[280px] rounded bg-gray-200 dark:bg-gray-700" />
          </div>
          <div className="h-7 w-14 rounded bg-gray-200 dark:bg-gray-700 shrink-0" />
        </li>
      ))}
    </ul>
  )
}

function PlatformLogo({
  platformId,
  className = 'h-5 w-5',
}: {
  platformId: AssetPlatformId
  className?: string
}) {
  const p = PLATFORM_BY_ID[platformId]
  return (
    <img
      src={p.logo}
      alt=""
      className={`object-contain shrink-0 ${className} ${p.darkInvert ? 'dark:invert dark:opacity-90' : ''}`}
      aria-hidden
    />
  )
}

function AssetLeadVisual({ link }: { link: DashboardAssetLink }) {
  if (dashboardAssetLinkLooksDemo(link)) {
    return (
      <span
        className="inline-flex h-9 min-w-[3rem] shrink-0 items-center justify-center rounded-md border border-gray-300/90 bg-gray-100 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-700 dark:border-gray-600 dark:bg-white/10 dark:text-gray-200"
        title="Demo"
      >
        Demo
      </span>
    )
  }
  return <PlatformLogo platformId={link.platform} className="h-6 w-6" />
}


export default function DashboardAssetsModal({ isOpen, onClose, userId }: Props) {
  const [items, setItems] = useState<DashboardAssetLink[]>([])
  const [showAddForm, setShowAddForm] = useState(false)
  const [platformDraft, setPlatformDraft] = useState<AssetPlatformId>('other')
  const [nameDraft, setNameDraft] = useState('')
  const [urlDraft, setUrlDraft] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [platformMenuOpen, setPlatformMenuOpen] = useState(false)
  const platformMenuRef = useRef<HTMLDivElement>(null)
  const [isSaving, setIsSaving] = useState(false)
  /** True until first successful fetch for this open (or handled error); used with cache for skeleton */
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [listSkeleton, setListSkeleton] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)

  const resetForm = () => {
    setPlatformDraft('other')
    setNameDraft('')
    setUrlDraft('')
    setFormError(null)
    setPlatformMenuOpen(false)
  }

  const openAddForm = () => {
    resetForm()
    setShowAddForm(true)
  }

  const cancelAddForm = () => {
    setShowAddForm(false)
    resetForm()
  }

  useEffect(() => {
    if (!isOpen) return
    setShowAddForm(false)
    setPlatformDraft('other')
    setNameDraft('')
    setUrlDraft('')
    setFormError(null)
    setPlatformMenuOpen(false)
    setLoadError(null)
    setSaveError(null)

    const cached = readCachedDashboardAssets(userId)
    const localSeed = userId ? readDashboardAssetLinks(userId) : []
    const seed = cached !== null ? cached : localSeed
    setItems(seed)
    setListSkeleton(cached === null && localSeed.length === 0)
    setIsRefreshing(true)

    let cancelled = false
    ;(async () => {
      try {
        let links = await fetchDashboardAssetLinksFromApi()
        if (cancelled) return
        if (links.length === 0 && userId) {
          const local = readDashboardAssetLinks(userId)
          if (local.length > 0) {
            await saveDashboardAssetLinksToApi(local)
            if (cancelled) return
            writeDashboardAssetLinks(userId, [])
            links = await fetchDashboardAssetLinksFromApi()
          }
        }
        if (!cancelled) {
          writeCachedDashboardAssets(userId, links)
          setItems(links)
          if (userId) writeDashboardAssetLinks(userId, links)
        }
      } catch {
        if (!cancelled) {
          setLoadError('Could not refresh from the server. Showing cached or device-saved links.')
          if (seed.length === 0) {
            setItems(userId ? readDashboardAssetLinks(userId) : [])
          }
        }
      } finally {
        if (!cancelled) {
          setListSkeleton(false)
          setIsRefreshing(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isOpen, userId])

  useEffect(() => {
    if (!platformMenuOpen) return
    const onDoc = (e: MouseEvent) => {
      if (platformMenuRef.current?.contains(e.target as Node)) return
      setPlatformMenuOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [platformMenuOpen])

  const persist = async (next: DashboardAssetLink[]) => {
    setSaveError(null)
    setIsSaving(true)
    try {
      const saved = await saveDashboardAssetLinksToApi(next)
      setItems(saved)
      writeCachedDashboardAssets(userId, saved)
      if (userId) writeDashboardAssetLinks(userId, saved)
    } catch {
      setSaveError('Could not save. Check your connection and try again.')
      throw new Error('save failed')
    } finally {
      setIsSaving(false)
    }
  }

  const handleAdd = async () => {
    setFormError(null)
    setSaveError(null)
    const name = nameDraft.trim()
    if (!name) {
      setFormError('Enter a name for this link.')
      return
    }
    const href = normalizeAssetUrl(urlDraft)
    if (!href) {
      setFormError('Enter a valid URL (e.g. https://example.com or drive.google.com/…).')
      return
    }
    const next = [{ id: newAssetId(), name, url: href, platform: platformDraft }, ...items]
    try {
      await persist(next)
      setShowAddForm(false)
      resetForm()
    } catch {
      /* saveError set */
    }
  }

  const handleRemove = async (id: string) => {
    setSaveError(null)
    try {
      await persist(items.filter((a) => a.id !== id))
    } catch {
      /* saveError set */
    }
  }

  const reorderItems = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return
    const next = [...items]
    const [removed] = next.splice(from, 1)
    next.splice(to, 0, removed)
    void persist(next)
  }

  const selectedPlatform = PLATFORM_BY_ID[platformDraft]

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Assets"
      description="Save quick links to decks, folders, or tools you use often. Synced to your account for the current artist profile."
      maxWidthClassName="max-w-lg"
      mobileCentered
    >
      <div className="space-y-6">
        {loadError ? <p className="text-sm text-amber-700 dark:text-amber-300">{loadError}</p> : null}
        {saveError ? <p className="text-sm text-red-600 dark:text-red-400">{saveError}</p> : null}

        {!showAddForm && (
          <div className="flex justify-end">
            <Button type="button" onClick={openAddForm} disabled={isSaving || listSkeleton}>
              Add link
            </Button>
          </div>
        )}

        {listSkeleton && !showAddForm ? <AssetsListSkeleton /> : null}

        {!listSkeleton && items.length === 0 && !showAddForm && (
          <p className="text-sm text-gray-500 dark:text-gray-400 -mt-2">
            No saved links yet. Use <span className="font-medium text-gray-700 dark:text-gray-300">Add link</span> to save one.
          </p>
        )}

        {!listSkeleton && items.length > 1 && !showAddForm ? (
          <p className="text-xs text-gray-500 dark:text-gray-400 -mt-1">Drag a row to reorder. Order is saved automatically.</p>
        ) : null}

        {!listSkeleton && items.length > 0 ? (
          <ul className={`space-y-2 ${isRefreshing ? 'opacity-70 transition-opacity' : ''}`}>
            {items.map((a, index) => {
              const isDragging = dragIndex === index
              const showDropBefore = dropIndex === index && dragIndex !== null && dragIndex !== index
              return (
                <li key={a.id} className="relative">
                  {showDropBefore ? (
                    <div
                      className="absolute -top-1 left-0 right-0 h-0.5 rounded-full bg-gray-900 dark:bg-gray-300 z-[1]"
                      aria-hidden
                    />
                  ) : null}
                  <div
                    draggable={!isSaving && items.length > 1}
                    onDragStart={(e) => {
                      if (items.length <= 1) return
                      setDragIndex(index)
                      e.dataTransfer.setData('text/plain', String(index))
                      e.dataTransfer.effectAllowed = 'move'
                    }}
                    onDragEnd={() => {
                      setDragIndex(null)
                      setDropIndex(null)
                    }}
                    onDragOver={(e) => {
                      if (dragIndex === null) return
                      e.preventDefault()
                      e.dataTransfer.dropEffect = 'move'
                      setDropIndex(index)
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      const raw = e.dataTransfer.getData('text/plain')
                      const from = parseInt(raw, 10)
                      if (Number.isNaN(from)) return
                      reorderItems(from, index)
                      setDragIndex(null)
                      setDropIndex(null)
                    }}
                    className={`flex items-center gap-2 sm:gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/40 px-2 sm:px-3 py-2.5 transition-shadow ${
                      items.length > 1 ? 'cursor-grab active:cursor-grabbing' : ''
                    } ${isDragging ? 'opacity-60 ring-2 ring-gray-400/50 dark:ring-gray-500/40' : ''}`}
                  >
                    {items.length > 1 ? (
                      <span
                        className="shrink-0 text-gray-400 dark:text-gray-500 touch-none select-none"
                        aria-hidden
                        title="Drag to reorder"
                      >
                        <GripVertical className="h-5 w-5" strokeWidth={1.5} />
                      </span>
                    ) : null}
                    <AssetLeadVisual link={a} />
                    <div className="flex-1 min-w-0">
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        draggable={false}
                        className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:underline truncate block"
                      >
                        {a.name}
                      </a>
                      <span className="text-xs text-gray-400 dark:text-gray-500 truncate block" title={a.url}>
                        {PLATFORM_BY_ID[a.platform].label} · {a.url.replace(/^https?:\/\//, '')}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleRemove(a.id)}
                      disabled={isSaving}
                      className="shrink-0 text-xs text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 px-2 py-1 rounded transition-colors disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        ) : null}

        {showAddForm && (
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-[rgb(var(--surface-2-rgb))] p-4 sm:p-5 space-y-4">
            <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">New link</h3>
            {formError ? <p className="text-sm text-red-600 dark:text-red-400">{formError}</p> : null}

            <div ref={platformMenuRef} className="relative">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Platform</span>
              <button
                type="button"
                id="asset-platform-trigger"
                aria-haspopup="listbox"
                aria-expanded={platformMenuOpen}
                onClick={() => setPlatformMenuOpen((o) => !o)}
                className={`${inputClass} flex items-center gap-2.5 text-left justify-between cursor-pointer`}
              >
                <span className="flex items-center gap-2.5 min-w-0">
                  <PlatformLogo platformId={platformDraft} className="h-5 w-5" />
                  <span className="truncate">{selectedPlatform.label}</span>
                </span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${platformMenuOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>
              {platformMenuOpen && (
                <ul
                  role="listbox"
                  aria-labelledby="asset-platform-trigger"
                  className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 py-1 shadow-lg"
                >
                  {PLATFORMS.map((p) => (
                    <li key={p.id} role="option" aria-selected={p.id === platformDraft}>
                      <button
                        type="button"
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700/80 ${
                          p.id === platformDraft ? 'bg-gray-100 dark:bg-gray-700/50' : ''
                        }`}
                        onClick={() => {
                          setPlatformDraft(p.id)
                          setPlatformMenuOpen(false)
                        }}
                      >
                        <PlatformLogo platformId={p.id} className="h-5 w-5" />
                        <span className="truncate text-gray-900 dark:text-gray-100">{p.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <label htmlFor="asset-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name
              </label>
              <input
                id="asset-name"
                type="text"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                placeholder="e.g. Stage plot PDF"
                className={inputClass}
                autoComplete="off"
              />
            </div>
            <div>
              <label htmlFor="asset-url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                URL
              </label>
              <input
                id="asset-url"
                type="text"
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
                placeholder="https://…"
                className={inputClass}
                autoComplete="url"
              />
            </div>
            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <Button type="button" variant="secondary" onClick={cancelAddForm} disabled={isSaving}>
                Cancel
              </Button>
              <Button type="button" onClick={() => void handleAdd()} disabled={isSaving}>
                {isSaving ? 'Saving…' : 'Save link'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
